<script setup lang="ts">
/** Saisie manuelle du cycle de vie d'une facture déposée chez le factor. */
import { computed, ref, watch } from 'vue'
import type { FactorInvoiceRow, FactorLifecyclePayload } from '@/v2/stores/useFactorDepositStore'

const props = defineProps<{ invoice: FactorInvoiceRow | null; loading?: boolean }>()
const emit = defineEmits<{
  close: []
  save: [payload: FactorLifecyclePayload]
}>()

const dialog = ref<HTMLDialogElement | null>(null)

const status = ref<'EXPORTED' | 'ACCEPTED' | 'REFUSED'>('EXPORTED')
const decisionAt = ref('')
const refusalReason = ref('')
const fundedByFactorAt = ref('')
const fundedAmount = ref<number | null>(null)
const factorSettledAt = ref('')
const notes = ref('')

/** ISO datetime → YYYY-MM-DD pour <input type="date">. */
function toDayInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : ''
}

watch(
  () => props.invoice,
  (inv) => {
    if (inv) {
      status.value = inv.factorStatus === 'NON_EXPORTE' ? 'EXPORTED' : inv.factorStatus
      decisionAt.value = toDayInput(inv.decisionAt)
      refusalReason.value = inv.refusalReason ?? ''
      fundedByFactorAt.value = toDayInput(inv.fundedByFactorAt)
      fundedAmount.value = inv.fundedAmount
      factorSettledAt.value = toDayInput(inv.factorSettledAt)
      notes.value = inv.notes ?? ''
      if (!dialog.value?.open) dialog.value?.showModal()
    } else {
      dialog.value?.close()
    }
  },
)

const isAccepted = computed(() => status.value === 'ACCEPTED')
const isRefused = computed(() => status.value === 'REFUSED')

function save() {
  const payload: FactorLifecyclePayload = {
    status: status.value,
    decisionAt: status.value === 'EXPORTED' ? null : decisionAt.value || null,
    refusalReason: isRefused.value ? refusalReason.value.trim() || null : null,
    fundedByFactorAt: isAccepted.value ? fundedByFactorAt.value || null : null,
    fundedAmount: isAccepted.value ? (fundedAmount.value ?? null) : null,
    factorSettledAt: isAccepted.value ? factorSettledAt.value || null : null,
    notes: notes.value.trim() || null,
  }
  emit('save', payload)
}

function onCancel() {
  emit('close')
}
</script>

<template>
  <dialog ref="dialog" class="modal" @close="onCancel">
    <div class="modal-box">
      <h3 class="text-lg font-semibold">Suivi factor</h3>
      <p v-if="invoice" class="mt-1 text-sm text-base-content/70">
        {{ invoice.invoiceName ?? invoice.invoiceUuid }} · {{ invoice.companyName ?? '—' }}
      </p>

      <div class="mt-4 space-y-4">
        <div>
          <label for="factor-status" class="mb-1 block text-sm font-medium text-base-content">Statut</label>
          <select id="factor-status" v-model="status" class="select select-bordered w-full text-base">
            <option value="EXPORTED">Déposée (en attente)</option>
            <option value="ACCEPTED">Acceptée</option>
            <option value="REFUSED">Refusée</option>
          </select>
        </div>

        <div v-if="isAccepted || isRefused">
          <label for="factor-decision" class="mb-1 block text-sm font-medium text-base-content">
            Date de décision
          </label>
          <input
            id="factor-decision"
            v-model="decisionAt"
            type="date"
            class="input input-bordered w-full tabular-nums"
          />
        </div>

        <div v-if="isRefused">
          <label for="factor-refusal" class="mb-1 block text-sm font-medium text-base-content">Motif de refus</label>
          <textarea
            id="factor-refusal"
            v-model="refusalReason"
            rows="2"
            class="textarea textarea-bordered w-full text-base"
            placeholder="Raison communiquée par le factor…"
          ></textarea>
        </div>

        <template v-if="isAccepted">
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label for="factor-funded-at" class="mb-1 block text-sm font-medium text-base-content">
                Argent reçu du factor le
              </label>
              <input
                id="factor-funded-at"
                v-model="fundedByFactorAt"
                type="date"
                class="input input-bordered w-full tabular-nums"
              />
            </div>
            <div>
              <label for="factor-funded-amount" class="mb-1 block text-sm font-medium text-base-content">
                Montant financé
              </label>
              <input
                id="factor-funded-amount"
                v-model.number="fundedAmount"
                type="number"
                step="0.01"
                min="0"
                class="input input-bordered w-full tabular-nums"
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <label for="factor-settled-at" class="mb-1 block text-sm font-medium text-base-content">
              Factor remboursé (par le client) le
            </label>
            <input
              id="factor-settled-at"
              v-model="factorSettledAt"
              type="date"
              class="input input-bordered w-full tabular-nums"
            />
          </div>
        </template>

        <div>
          <label for="factor-notes" class="mb-1 block text-sm font-medium text-base-content">Notes</label>
          <textarea
            id="factor-notes"
            v-model="notes"
            rows="2"
            class="textarea textarea-bordered w-full text-base"
            placeholder="Notes internes…"
          ></textarea>
        </div>
      </div>

      <div class="modal-action">
        <button type="button" class="btn btn-ghost" :disabled="loading" @click="onCancel">Annuler</button>
        <button type="button" class="btn btn-primary" :disabled="loading" :aria-busy="loading" @click="save">
          <span v-if="loading" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
          Enregistrer
        </button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="onCancel">Fermer</button>
    </form>
  </dialog>
</template>
